import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  FileText, 
  Search, 
  Filter, 
  ArrowDownCircle, 
  ArrowUpCircle,
  Calendar,
  Building2,
  Download,
  Eye,
  ExternalLink,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileSpreadsheet,
  X,
  Receipt
} from 'lucide-react';
import { toast } from 'sonner';

const AccountingFaturalar = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('alinan');
  const [girisler, setGirisler] = useState([]);
  const [cikislar, setCikislar] = useState([]);
  const [cariHesaplar, setCariHesaplar] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtreler
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterDurum, setFilterDurum] = useState('all');
  const [filterCari, setFilterCari] = useState('all');
  const [filterMinTutar, setFilterMinTutar] = useState('');
  const [filterMaxTutar, setFilterMaxTutar] = useState('');
  
  // Sıralama
  const [sortConfig, setSortConfig] = useState({ key: 'tarih', direction: 'desc' });
  
  // Modal
  const [showBelgeModal, setShowBelgeModal] = useState(false);
  const [previewBelgeler, setPreviewBelgeler] = useState([]);
  const [previewTitle, setPreviewTitle] = useState('');
  
  // Detay Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedFatura, setSelectedFatura] = useState(null);
  const [takipBelgeler, setTakipBelgeler] = useState([]);
  const [loadingTakip, setLoadingTakip] = useState(false);
  
  // Takip verileri
  const [alacakTakipler, setAlacakTakipler] = useState([]);
  const [borcTakipler, setBorcTakipler] = useState([]);
  
  // Eksik Fatura Ekleme Modal
  const [showEksikFaturaModal, setShowEksikFaturaModal] = useState(false);
  const [eksikFaturaSearchTerm, setEksikFaturaSearchTerm] = useState('');
  const [selectedEksikFatura, setSelectedEksikFatura] = useState(null);
  const [yeniFaturaMiktari, setYeniFaturaMiktari] = useState('');
  const [yeniFaturaKdvOrani, setYeniFaturaKdvOrani] = useState('20');
  const [yeniFaturaKdv, setYeniFaturaKdv] = useState('');
  const [yeniFaturaTarihi, setYeniFaturaTarihi] = useState(new Date().toISOString().split('T')[0]);
  const [yeniFaturaAciklama, setYeniFaturaAciklama] = useState('');
  const [yeniFaturaBelge, setYeniFaturaBelge] = useState(null);
  const [savingFatura, setSavingFatura] = useState(false);
  
  // Açıklama Modal
  const [showAciklamaModal, setShowAciklamaModal] = useState(false);
  const [selectedAciklama, setSelectedAciklama] = useState('');

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('fortis_token');
        const headers = { 'Authorization': `Bearer ${token}` };
        
        // Fetch girişler
        const girisResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/accounting/girisler`,
          { headers }
        );
        if (girisResponse.ok) {
          const girisData = await girisResponse.json();
          setGirisler(girisData);
        }
        
        // Fetch çıkışlar
        const cikisResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar`,
          { headers }
        );
        if (cikisResponse.ok) {
          const cikisData = await cikisResponse.json();
          setCikislar(cikisData);
        }

        // Fetch cari hesaplar
        const cariResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/global/cari-hesaplar`,
          { headers }
        );
        if (cariResponse.ok) {
          const cariData = await cariResponse.json();
          setCariHesaplar(cariData);
        }
        
        // Fetch alacak takipler
        const alacakResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/accounting/takipler/alacak`,
          { headers }
        );
        if (alacakResponse.ok) {
          const alacakData = await alacakResponse.json();
          setAlacakTakipler(alacakData);
        }
        
        // Fetch borç takipler
        const borcResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/accounting/takipler/borc`,
          { headers }
        );
        if (borcResponse.ok) {
          const borcData = await borcResponse.json();
          setBorcTakipler(borcData);
        }
      } catch (error) {
        console.error('Veriler yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Format currency
  const formatCurrency = (amount, currency = 'TL') => {
    if (!amount && amount !== 0) return '-';
    const symbols = { TL: '₺', USD: '$', EUR: '€' };
    return `${symbols[currency] || ''}${Number(amount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  // Format date with time
  const formatDateTime = (dateStr, createdAt) => {
    if (!dateStr) return { date: '-', time: '' };
    const d = new Date(dateStr);
    // Saat için created_at kullan (işlem saati)
    let time = '';
    if (createdAt) {
      const c = new Date(createdAt);
      time = c.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    }
    return {
      date: d.toLocaleDateString('tr-TR'),
      time: time
    };
  };

  // Get all belgeler for a record
  const getAllBelgeler = (record) => {
    const allBelgeler = [];
    
    if (record.belgeler && record.belgeler.length > 0) {
      record.belgeler.forEach((b, i) => allBelgeler.push({ ...b, type: 'Belge', index: i + 1 }));
    }
    
    if (record.dekont_url) {
      allBelgeler.push({ url: record.dekont_url, name: record.dekont_adi || 'Dekont', type: 'Dekont' });
    }
    
    if (record.fatura_belgesi) {
      allBelgeler.push({ url: record.fatura_belgesi, name: 'Fatura Belgesi', type: 'Fatura' });
    }
    
    if (record.kesilen_faturalar && record.kesilen_faturalar.length > 0) {
      record.kesilen_faturalar.forEach((f, i) => {
        if (f.belge) {
          // Belge URL'si veya obje olabilir
          const belgeUrl = f.belge.url || f.belge;
          const belgeName = f.belge.name || (f.tarih ? `Fatura ${formatDate(f.tarih)}` : `Fatura #${i + 1}`);
          allBelgeler.push({ 
            url: belgeUrl, 
            name: belgeName, 
            type: 'Kesilen Fatura', 
            tarih: f.tarih,
            miktar: f.miktar
          });
        }
      });
    }
    
    if (record.alinan_faturalar && record.alinan_faturalar.length > 0) {
      record.alinan_faturalar.forEach((f, i) => {
        if (f.belge) {
          const belgeUrl = f.belge.url || f.belge;
          const belgeName = f.belge.name || (f.tarih ? `Fatura ${formatDate(f.tarih)}` : `Fatura #${i + 1}`);
          allBelgeler.push({ 
            url: belgeUrl, 
            name: belgeName, 
            type: 'Alınan Fatura', 
            tarih: f.tarih,
            miktar: f.miktar
          });
        }
      });
    }
    
    return allBelgeler;
  };

  // Open belgeler preview
  const openBelgePreview = (record, title) => {
    const belgeler = getAllBelgeler(record);
    setPreviewBelgeler(belgeler);
    setPreviewTitle(title);
    setShowBelgeModal(true);
  };

  // Delete fatura - clears fatura data from the parent record
  const handleDeleteFatura = async (fatura, type) => {
    try {
      const token = localStorage.getItem('fortis_token');
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const endpoint = type === 'alinan' 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar/${fatura.id}/fatura-durumu`
        : `${process.env.REACT_APP_BACKEND_URL}/api/accounting/girisler/${fatura.id}/fatura-durumu`;
      
      // Clear fatura status
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ fatura_durumu: 'fatura_bekleniyor' })
      });
      
      if (response.ok) {
        toast.success('Fatura kaydı silindi');
        // Refresh data
        window.location.reload();
      } else {
        toast.error('Fatura silinemedi');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Bir hata oluştu');
    }
  };

  // Edit fatura - opens detail modal for editing
  const handleEditFatura = (fatura, type) => {
    openDetailModal(fatura, type);
  };

  // Open detail modal with takip belgeler
  const openDetailModal = async (fatura, type) => {
    setSelectedFatura({ ...fatura, type });
    setShowDetailModal(true);
    setTakipBelgeler([]);
    
    // Fetch takip belgeler if linked
    const takipId = type === 'alinan' ? fatura.borc_takip_id : fatura.alacak_takip_id;
    if (takipId) {
      setLoadingTakip(true);
      try {
        const token = localStorage.getItem('fortis_token');
        const endpoint = type === 'alinan' 
          ? `${process.env.REACT_APP_BACKEND_URL}/api/accounting/borc-takip/${takipId}`
          : `${process.env.REACT_APP_BACKEND_URL}/api/accounting/alacak-takip/${takipId}`;
        
        const response = await fetch(endpoint, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const takipData = await response.json();
          if (takipData.belgeler && takipData.belgeler.length > 0) {
            setTakipBelgeler(takipData.belgeler.map((b, i) => ({
              ...b,
              type: 'Takip Belgesi',
              index: i + 1
            })));
          }
        }
      } catch (error) {
        console.error('Takip belgeler yüklenirken hata:', error);
      } finally {
        setLoadingTakip(false);
      }
    }
  };

  // Get unique months for filter
  const getMonths = () => {
    const months = new Set();
    [...girisler, ...cikislar].forEach(item => {
      if (item.tarih) {
        const date = new Date(item.tarih);
        months.add(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
      }
    });
    return Array.from(months).sort().reverse();
  };

  // Get unique durumlar
  const getDurumlar = () => {
    const durumlar = new Set();
    [...girisler, ...cikislar].forEach(item => {
      if (item.fatura_durumu) {
        durumlar.add(item.fatura_durumu);
      }
    });
    return Array.from(durumlar);
  };

  // Get unique cari hesaplar from faturalar
  const getUniqueCariHesaplar = () => {
    const cariSet = new Set();
    [...girisler, ...cikislar].forEach(item => {
      const cari = item.cari_hesap_adi || item.kimden || item.kime;
      if (cari) cariSet.add(cari);
    });
    return Array.from(cariSet).sort();
  };

  // Sıralama fonksiyonu
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-indigo-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-indigo-600" />;
  };

  // Sıralama uygula
  const sortData = (data) => {
    return [...data].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'tarih':
          aVal = new Date(a.fatura_tarihi || a.tarih || 0);
          bVal = new Date(b.fatura_tarihi || b.tarih || 0);
          break;
        case 'fatura_no':
          aVal = a.fatura_no || a.islem_no || '';
          bVal = b.fatura_no || b.islem_no || '';
          break;
        case 'cari':
          aVal = a.cari_hesap_adi || a.kimden || a.kime || '';
          bVal = b.cari_hesap_adi || b.kimden || b.kime || '';
          break;
        case 'durum':
          aVal = a.fatura_durumu || '';
          bVal = b.fatura_durumu || '';
          break;
        case 'tutar':
          aVal = parseFloat(a.miktar) || 0;
          bVal = parseFloat(b.miktar) || 0;
          break;
        case 'fatura_tutari':
          aVal = parseFloat(a.fatura_miktari || a.kesilen_fatura_miktari || a.kesilen_fatura_toplam) || 0;
          bVal = parseFloat(b.fatura_miktari || b.kesilen_fatura_miktari || b.kesilen_fatura_toplam) || 0;
          break;
        default:
          aVal = a[sortConfig.key] || '';
          bVal = b[sortConfig.key] || '';
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Filter kesilen faturalar (Girişlerden - Para geldi, biz fatura kestik)
  const kesilenFaturalar = useMemo(() => {
    const flattenedFaturalar = [];
    
    girisler.forEach(g => {
      if (g.kesilen_faturalar && g.kesilen_faturalar.length > 0) {
        g.kesilen_faturalar.forEach((fatura, index) => {
          flattenedFaturalar.push({
            ...g,
            fatura_id: fatura.id,
            fatura_index: index + 1,
            fatura_miktar: fatura.miktar,
            fatura_kdv: fatura.kdv_tutari,
            fatura_tarih: fatura.tarih,
            fatura_aciklama: fatura.aciklama,
            fatura_belge: fatura.belge,
            fatura_ekleme_tarihi: fatura.ekleme_tarihi,
            row_id: `${g.id}_fatura_${index}`,
            is_individual_fatura: true
          });
        });
      } else {
        const validStatuses = ['fatura_kesildi', 'eksik_fatura', 'faturali', 'fis'];
        if (validStatuses.includes(g.fatura_durumu)) {
          flattenedFaturalar.push({
            ...g,
            fatura_miktar: parseFloat(g.fatura_miktari || g.kesilen_fatura_miktari || g.miktar || 0),
            fatura_kdv: parseFloat(g.kdv_tutari || 0),
            fatura_tarih: g.tarih,
            row_id: g.id,
            is_individual_fatura: false
          });
        }
      }
    });
    
    // Filtreleme uygula
    let filtered = flattenedFaturalar.filter(f => {
      const matchesSearch = searchTerm === '' || 
        f.aciklama?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.kimden?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.cari_hesap_adi?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.fatura_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.seri_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.fatura_aciklama?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const faturaTarih = f.fatura_tarih || f.tarih;
      const matchesMonth = filterMonth === 'all' || 
        (faturaTarih && faturaTarih.startsWith(filterMonth));

      const matchesDurum = filterDurum === 'all' || f.fatura_durumu === filterDurum;

      const cariName = f.cari_hesap_adi || f.kimden;
      const matchesCari = filterCari === 'all' || cariName === filterCari;

      const tutar = parseFloat(f.fatura_miktar) || 0;
      const matchesMinTutar = filterMinTutar === '' || tutar >= parseFloat(filterMinTutar);
      const matchesMaxTutar = filterMaxTutar === '' || tutar <= parseFloat(filterMaxTutar);
      
      return matchesSearch && matchesMonth && matchesDurum && matchesCari && matchesMinTutar && matchesMaxTutar;
    });

    // Tarihe göre sırala (en yeni en üstte)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.fatura_ekleme_tarihi || a.fatura_tarih || a.tarih || 0);
      const dateB = new Date(b.fatura_ekleme_tarihi || b.fatura_tarih || b.tarih || 0);
      return dateB - dateA;
    });
  }, [girisler, searchTerm, filterMonth, filterDurum, filterCari, filterMinTutar, filterMaxTutar, sortConfig]);

  // Filter alınan faturalar (Çıkışlardan - Para gitti, bize fatura geldi)
  const alinanFaturalar = useMemo(() => {
    const flattenedFaturalar = [];
    
    cikislar.forEach(c => {
      if (c.alinan_faturalar && c.alinan_faturalar.length > 0) {
        c.alinan_faturalar.forEach((fatura, index) => {
          flattenedFaturalar.push({
            ...c,
            fatura_id: fatura.id,
            fatura_index: index + 1,
            fatura_miktar: fatura.miktar,
            fatura_kdv: fatura.kdv_tutari,
            fatura_tarih: fatura.tarih,
            fatura_aciklama: fatura.aciklama,
            fatura_belge: fatura.belge,
            fatura_ekleme_tarihi: fatura.ekleme_tarihi,
            row_id: `${c.id}_fatura_${index}`,
            is_individual_fatura: true
          });
        });
      } else {
        const validStatuses = ['fatura_alindi', 'eksik_fatura', 'faturali', 'fis'];
        if (validStatuses.includes(c.fatura_durumu)) {
          flattenedFaturalar.push({
            ...c,
            fatura_miktar: parseFloat(c.fatura_miktari || c.miktar || 0),
            fatura_kdv: parseFloat(c.kdv_tutari || 0),
            fatura_tarih: c.tarih,
            row_id: c.id,
            is_individual_fatura: false
          });
        }
      }
    });
    
    // Filtreleme uygula
    let filtered = flattenedFaturalar.filter(f => {
      const matchesSearch = searchTerm === '' || 
        f.aciklama?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.kime?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.cari_hesap_adi?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.fatura_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.seri_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.fatura_aciklama?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const faturaTarih = f.fatura_tarih || f.tarih;
      const matchesMonth = filterMonth === 'all' || 
        (faturaTarih && faturaTarih.startsWith(filterMonth));

      const matchesDurum = filterDurum === 'all' || f.fatura_durumu === filterDurum;

      const cariName = f.cari_hesap_adi || f.kime;
      const matchesCari = filterCari === 'all' || cariName === filterCari;

      const tutar = parseFloat(f.fatura_miktar) || 0;
      const matchesMinTutar = filterMinTutar === '' || tutar >= parseFloat(filterMinTutar);
      const matchesMaxTutar = filterMaxTutar === '' || tutar <= parseFloat(filterMaxTutar);
      
      return matchesSearch && matchesMonth && matchesDurum && matchesCari && matchesMinTutar && matchesMaxTutar;
    });

    // Tarihe göre sırala (en yeni en üstte)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.fatura_ekleme_tarihi || a.fatura_tarih || a.tarih || 0);
      const dateB = new Date(b.fatura_ekleme_tarihi || b.fatura_tarih || b.tarih || 0);
      return dateB - dateA;
    });
  }, [cikislar, searchTerm, filterMonth, filterDurum, filterCari, filterMinTutar, filterMaxTutar, sortConfig]);

  // Calculate totals
  const kesilenToplam = kesilenFaturalar.reduce((sum, f) => sum + (parseFloat(f.fatura_miktar) || 0), 0);
  const alinanToplam = alinanFaturalar.reduce((sum, f) => sum + (parseFloat(f.fatura_miktar) || 0), 0);
  
  // KDV toplamları
  const kesilenKdvToplam = kesilenFaturalar.reduce((sum, f) => sum + (parseFloat(f.fatura_kdv) || 0), 0);
  const alinanKdvToplam = alinanFaturalar.reduce((sum, f) => sum + (parseFloat(f.fatura_kdv) || 0), 0);
  
  // Anlaşılan toplam miktarlar (tüm kayıtlardan)
  const toplamAlinmasiGereken = cikislar.reduce((sum, c) => sum + (parseFloat(c.anlaşılan_fatura_miktari) || 0), 0);
  const toplamKesilmesiGereken = girisler.reduce((sum, g) => sum + (parseFloat(g.anlaşılan_fatura_miktari) || 0), 0);

  // Alınacak faturalar - Çıkışlardan alınması gereken faturalar (fatura_alinacak veya eksik_fatura'nın kalan kısmı)
  const alinacakFaturalar = useMemo(() => {
    const list = [];
    cikislar.forEach(c => {
      const isAlinacak = c.fatura_durumu === 'fatura_alinacak';
      
      if (isAlinacak) {
        list.push({
          ...c,
          fatura_miktar: parseFloat(c.anlaşılan_fatura_miktari) || parseFloat(c.miktar) || 0,
          cari_hesap: c.cari_hesap_adi,
          islem_no: c.seri_no,
          type: 'alinacak'
        });
      }
    });
    return list;
  }, [cikislar]);

  // Alınacak fatura toplamı
  const alinacakToplam = alinacakFaturalar.reduce((sum, f) => sum + (parseFloat(f.fatura_miktar) || 0), 0);

  // Kesilecek faturalar - Girişlerden kesilmesi gereken faturalar (fatura_kesilecek veya eksik_fatura'nın kalan kısmı)
  const kesilecekFaturalar = useMemo(() => {
    const list = [];
    girisler.forEach(g => {
      const isKesilecek = g.fatura_durumu === 'fatura_kesilecek';
      
      if (isKesilecek) {
        list.push({
          ...g,
          fatura_miktar: parseFloat(g.anlaşılan_fatura_miktari) || parseFloat(g.miktar) || 0,
          cari_hesap: g.cari_hesap_adi,
          islem_no: g.seri_no,
          type: 'kesilecek'
        });
      }
    });
    return list;
  }, [girisler]);

  // Kesilecek fatura toplamı
  const kesilecekToplam = kesilecekFaturalar.reduce((sum, f) => sum + (parseFloat(f.fatura_miktar) || 0), 0);

  // Bu ayki KDV hesaplaması
  const buAyKdvDurumu = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    let tahsilEdilenKDV = 0; // Kesilen (Giriş)
    kesilenFaturalar.forEach(f => {
      const d = new Date(f.fatura_tarih || f.tarih || 0);
      if (d.getMonth() === currentMonth && d.getFullYear() === currentYear) {
        tahsilEdilenKDV += parseFloat(f.fatura_kdv || f.kdv_tutari) || 0;
      }
    });

    let odenenKDV = 0; // Alınan (Çıkış)
    alinanFaturalar.forEach(f => {
      const d = new Date(f.fatura_tarih || f.tarih || 0);
      if (d.getMonth() === currentMonth && d.getFullYear() === currentYear) {
        odenenKDV += parseFloat(f.fatura_kdv || f.kdv_tutari) || 0;
      }
    });

    const netKDV = tahsilEdilenKDV - odenenKDV; // Pozitifse ödenecek KDV (Açık), negatifse devreden KDV (Artı)
    return {
      tahsilEdilenKDV,
      odenenKDV,
      netKDV
    };
  }, [kesilenFaturalar, alinanFaturalar]);

  // Eksik fatura toplamları (Alınan ve Kesilen için - sadece kısmi veya tam kesilmiş/alınmış olanlar)
  const eksikAlinanToplam = useMemo(() => {
    return cikislar.reduce((sum, c) => {
      const validStatuses = ['fatura_alindi', 'eksik_fatura', 'faturali', 'fis'];
      if (!validStatuses.includes(c.fatura_durumu)) return sum;
      
      const anlasilan = parseFloat(c.anlaşılan_fatura_miktari) || 0;
      const alinan = parseFloat(c.alinan_fatura_toplam || c.fatura_miktari) || 0;
      const eksik = anlasilan - alinan;
      return sum + (anlasilan > 0 && eksik > 0 ? eksik : 0);
    }, 0);
  }, [cikislar]);

  const eksikKesilenToplam = useMemo(() => {
    return girisler.reduce((sum, g) => {
      const validStatuses = ['fatura_kesildi', 'eksik_fatura', 'faturali', 'fis'];
      if (!validStatuses.includes(g.fatura_durumu)) return sum;
      
      const anlasilan = parseFloat(g.anlaşılan_fatura_miktari) || 0;
      const kesilen = parseFloat(g.kesilen_fatura_toplam || g.fatura_miktari) || 0;
      const eksik = anlasilan - kesilen;
      return sum + (anlasilan > 0 && eksik > 0 ? eksik : 0);
    }, 0);
  }, [girisler]);

  // Eksik faturaları hesapla (anlaşılan > kesilen/alınan olan kayıtlar)
  const eksikKesilenFaturalar = useMemo(() => {
    return girisler
      .filter(g => {
        const validStatuses = ['fatura_kesildi', 'eksik_fatura', 'faturali', 'fis'];
        if (!validStatuses.includes(g.fatura_durumu)) return false;
        
        const anlasilan = parseFloat(g.anlaşılan_fatura_miktari) || 0;
        const kesilen = parseFloat(g.kesilen_fatura_toplam || g.fatura_miktari) || 0;
        const eksik = anlasilan - kesilen;
        return anlasilan > 0 && eksik > 0;
      })
      .map(g => {
        const anlasilan = parseFloat(g.anlaşılan_fatura_miktari) || 0;
        const kesilen = parseFloat(g.kesilen_fatura_toplam || g.fatura_miktari) || 0;
        const eksik = anlasilan - kesilen;
        // Takip bilgisini bul
        const takip = g.alacak_takip_id ? alacakTakipler.find(t => t.id === g.alacak_takip_id) : null;
        return {
          ...g,
          type: 'kesilen',
          anlasilanMiktar: anlasilan,
          kesilenMiktar: kesilen,
          eksikMiktar: eksik,
          takipSeriNo: takip?.seri_no || g.seri_no,
          displayName: g.cari_hesap_adi || 'Bilinmeyen'
        };
      })
      .filter(g => {
        if (!eksikFaturaSearchTerm) return true;
        const search = eksikFaturaSearchTerm.toLowerCase();
        return (
          (g.seri_no || '').toLowerCase().includes(search) ||
          (g.takipSeriNo || '').toLowerCase().includes(search) ||
          (g.cari_hesap_adi || '').toLowerCase().includes(search) ||
          (g.displayName || '').toLowerCase().includes(search)
        );
      });
  }, [girisler, alacakTakipler, eksikFaturaSearchTerm]);

  const eksikAlinanFaturalar = useMemo(() => {
    return cikislar
      .filter(c => {
        const validStatuses = ['fatura_alindi', 'eksik_fatura', 'faturali', 'fis'];
        if (!validStatuses.includes(c.fatura_durumu)) return false;
        
        const anlasilan = parseFloat(c.anlaşılan_fatura_miktari) || 0;
        const alinan = parseFloat(c.alinan_fatura_toplam || c.fatura_miktari) || 0;
        const eksik = anlasilan - alinan;
        return anlasilan > 0 && eksik > 0;
      })
      .map(c => {
        const anlasilan = parseFloat(c.anlaşılan_fatura_miktari) || 0;
        const alinan = parseFloat(c.alinan_fatura_toplam || c.fatura_miktari) || 0;
        const eksik = anlasilan - alinan;
        // Takip bilgisini bul
        const takip = c.borc_takip_id ? borcTakipler.find(t => t.id === c.borc_takip_id) : null;
        return {
          ...c,
          type: 'alinan',
          anlasilanMiktar: anlasilan,
          alinanMiktar: alinan,
          eksikMiktar: eksik,
          takipSeriNo: takip?.seri_no || c.seri_no,
          displayName: c.cari_hesap_adi || 'Bilinmeyen'
        };
      })
      .filter(c => {
        if (!eksikFaturaSearchTerm) return true;
        const search = eksikFaturaSearchTerm.toLowerCase();
        return (
          (c.seri_no || '').toLowerCase().includes(search) ||
          (c.takipSeriNo || '').toLowerCase().includes(search) ||
          (c.cari_hesap_adi || '').toLowerCase().includes(search) ||
          (c.displayName || '').toLowerCase().includes(search)
        );
      });
  }, [cikislar, borcTakipler, eksikFaturaSearchTerm]);

  // Alınacak faturalar listesi - Eksik Fatura Modal için (fatura_durumu === 'fatura_kesilecek' veya 'fatura_alinacak')
  const eksikAlinacakFaturalar = useMemo(() => {
    return cikislar
      .filter(c => c.fatura_durumu === 'fatura_alinacak')
      .map(c => {
        const anlasilan = parseFloat(c.anlaşılan_fatura_miktari) || parseFloat(c.miktar) || 0;
        const takip = c.borc_takip_id ? borcTakipler.find(t => t.id === c.borc_takip_id) : null;
        return {
          ...c,
          type: 'alinacak',
          anlasilanMiktar: anlasilan,
          alinacakMiktar: anlasilan,
          takipSeriNo: takip?.seri_no || c.seri_no,
          displayName: c.cari_hesap_adi || 'Bilinmeyen'
        };
      })
      .filter(c => {
        if (!eksikFaturaSearchTerm) return true;
        const search = eksikFaturaSearchTerm.toLowerCase();
        return (
          (c.seri_no || '').toLowerCase().includes(search) ||
          (c.takipSeriNo || '').toLowerCase().includes(search) ||
          (c.cari_hesap_adi || '').toLowerCase().includes(search) ||
          (c.displayName || '').toLowerCase().includes(search)
        );
      });
  }, [cikislar, borcTakipler, eksikFaturaSearchTerm]);

  // Kesilecek faturalar listesi - Eksik Fatura Modal için (girişlerden kesilecek)
  const eksikKesilecekFaturalar = useMemo(() => {
    return girisler
      .filter(g => g.fatura_durumu === 'fatura_kesilecek')
      .map(g => {
        const anlasilan = parseFloat(g.anlaşılan_fatura_miktari) || parseFloat(g.miktar) || 0;
        const takip = g.alacak_takip_id ? alacakTakipler.find(t => t.id === g.alacak_takip_id) : null;
        return {
          ...g,
          type: 'kesilecek',
          anlasilanMiktar: anlasilan,
          kesilecekMiktar: anlasilan,
          takipSeriNo: takip?.seri_no || g.seri_no,
          displayName: g.cari_hesap_adi || 'Bilinmeyen'
        };
      })
      .filter(g => {
        if (!eksikFaturaSearchTerm) return true;
        const search = eksikFaturaSearchTerm.toLowerCase();
        return (
          (g.seri_no || '').toLowerCase().includes(search) ||
          (g.takipSeriNo || '').toLowerCase().includes(search) ||
          (g.cari_hesap_adi || '').toLowerCase().includes(search) ||
          (g.displayName || '').toLowerCase().includes(search)
        );
      });
  }, [girisler, alacakTakipler, eksikFaturaSearchTerm]);

  const kdvOdemeleri = useMemo(() => {
    const list = [];
    
    // Add islem_tipi to easily identify if it is giris or cikis
    girisler.forEach(g => {
      const etiket = (g.etiket_adi || '').toLowerCase();
      if (etiket.includes('kdv ödemesi') || etiket.includes('kdv odemesi') || etiket.includes('kdv ödemeleri')) {
        list.push({ ...g, islem_tipi: 'Giriş (Tahsilat)' });
      }
    });

    cikislar.forEach(c => {
      const etiket = (c.etiket_adi || '').toLowerCase();
      if (etiket.includes('kdv ödemesi') || etiket.includes('kdv odemesi') || etiket.includes('kdv ödemeleri')) {
        list.push({ ...c, islem_tipi: 'Çıkış (Ödeme)' });
      }
    });

    return list.sort((a, b) => new Date(b.tarih || b.created_at || 0) - new Date(a.tarih || a.created_at || 0));
  }, [girisler, cikislar]);

  // Eksik fatura ekleme fonksiyonu
  const handleAddEksikFatura = async () => {
    if (!selectedEksikFatura || !yeniFaturaMiktari) return;
    
    // KDV validasyonu - KDV tutarı fatura tutarından büyük olamaz
    // Matrah hesaplanıyor: Girilen miktar (KDV Dahil) - KDV Tutarı = Matrah
    const faturaMiktarKdvDahil = parseFloat(yeniFaturaMiktari) || 0;
    const kdvTutar = parseFloat(yeniFaturaKdv) || 0;
    const faturaMiktarMatrah = faturaMiktarKdvDahil - kdvTutar;
    
    if (kdvTutar > faturaMiktarKdvDahil) {
      toast.error('KDV tutarı fatura tutarından büyük olamaz');
      return;
    }
    
    setSavingFatura(true);
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Endpoint seçimi: kesilen ve kesilecek -> girisler, alinan ve alinacak -> cikislar
      let endpoint;
      if (selectedEksikFatura.type === 'kesilen' || selectedEksikFatura.type === 'kesilecek') {
        endpoint = `${process.env.REACT_APP_BACKEND_URL}/api/accounting/girisler/${selectedEksikFatura.id}/fatura`;
      } else {
        // alinan veya alinacak
        endpoint = `${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar/${selectedEksikFatura.id}/fatura`;
      }
      
      // FormData kullan (belge yüklemesi için)
      const formData = new FormData();
      formData.append('fatura_miktari', faturaMiktarKdvDahil);
      formData.append('kdv_tutari', kdvTutar);
      formData.append('fatura_tarihi', yeniFaturaTarihi);
      formData.append('aciklama', yeniFaturaAciklama || '');
      if (yeniFaturaBelge) {
        formData.append('belge', yeniFaturaBelge);
      }
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        // Başarı bildirimi göster
        toast.success(`Fatura başarıyla eklendi: ${formatCurrency(faturaMiktarKdvDahil, selectedEksikFatura.para_birimi || 'TL')}`);
        
        // Verileri yenile
        const girisResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/accounting/girisler`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (girisResponse.ok) {
          setGirisler(await girisResponse.json());
        }
        
        const cikisResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/accounting/cikislar`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (cikisResponse.ok) {
          setCikislar(await cikisResponse.json());
        }
        
        // Modal'ı kapat ve formu temizle
        setShowEksikFaturaModal(false);
        setSelectedEksikFatura(null);
        setYeniFaturaMiktari('');
        setYeniFaturaKdvOrani('20');
        setYeniFaturaKdv('');
        setYeniFaturaTarihi(new Date().toISOString().split('T')[0]);
        setYeniFaturaAciklama('');
        setYeniFaturaBelge(null);
        setEksikFaturaSearchTerm('');
      } else {
        toast.error('Fatura eklenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Fatura ekleme hatası:', error);
      toast.error('Fatura eklenirken bir hata oluştu');
    } finally {
      setSavingFatura(false);
    }
  };

  // Fatura durumu badge
  const getFaturaDurumuBadge = (durum) => {
    const durumlar = {
      'fatura_kesildi': { label: 'Fatura Kesildi', className: 'bg-green-500' },
      'fatura_kesilecek': { label: 'Fatura Kesilecek', className: 'bg-yellow-500' },
      'borc_alindi': { label: 'Borç Alındı', className: 'bg-blue-500 text-white' },
      'fatura_alindi': { label: 'Fatura Alındı', className: 'bg-emerald-500' },
      'odendi': { label: 'Ödendi', className: 'bg-emerald-500' }
    };
    const config = durumlar[durum] || { label: durum || '-', className: '' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  // Durum label
  const getDurumLabel = (durum) => {
    const labels = {
      'fatura_kesildi': 'Fatura Kesildi',
      'fatura_kesilecek': 'Fatura Kesilecek',
      'borc_alindi': 'Borç Alındı',
      'fatura_alindi': 'Fatura Alındı',
      'odendi': 'Ödendi'
    };
    return labels[durum] || durum;
  };

  // Excel export fonksiyonu
  const exportToExcel = useCallback((data, type) => {
    // Kesilen = Girişlerden (para geldi, biz fatura kestik) → Kimden aldık
    // Alınan = Çıkışlardan (para gitti, bize fatura geldi) → Kime ödedik
    const headers = type === 'kesilen' 
      ? ['Tarih', 'Fatura No', 'Kimden', 'Açıklama', 'Durum', 'Giriş Tutarı', 'Fatura Tutarı', 'KDV Tutarı', 'Para Birimi']
      : ['Tarih', 'Fatura No', 'Kime', 'Açıklama', 'Durum', 'Çıkış Tutarı', 'Fatura Tutarı', 'KDV Tutarı', 'Para Birimi'];

    const rows = data.map(f => {
      const cari = f.cari_hesap_adi || f.kimden || f.kime || '-';
      const tutar = parseFloat(f.miktar) || 0;
      const faturaTutari = parseFloat(f.fatura_miktar || f.kesilen_fatura_toplam || f.fatura_miktari || f.kesilen_fatura_miktari) || 0;
      const kdvTutari = parseFloat(f.fatura_kdv || f.kdv_tutari) || 0;
      
      return [
        formatDate(f.fatura_tarih || f.fatura_tarihi || f.tarih),
        f.fatura_no || f.islem_no || '-',
        cari,
        f.fatura_aciklama || f.aciklama || '-',
        getDurumLabel(f.fatura_durumu),
        tutar.toFixed(2),
        faturaTutari.toFixed(2),
        kdvTutari.toFixed(2),
        f.para_birimi || 'TL'
      ];
    });

    // BOM for UTF-8
    const BOM = '\uFEFF';
    
    // Create CSV content
    const csvContent = BOM + [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const fileName = type === 'kesilen' 
      ? `kesilen_faturalar_${new Date().toISOString().split('T')[0]}.csv`
      : `alinan_faturalar_${new Date().toISOString().split('T')[0]}.csv`;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // Filtreleri temizle
  const clearFilters = () => {
    setSearchTerm('');
    setFilterMonth('all');
    setFilterDurum('all');
    setFilterCari('all');
    setFilterMinTutar('');
    setFilterMaxTutar('');
  };

  // Aktif filtre sayısı
  const activeFilterCount = [
    searchTerm !== '',
    filterMonth !== 'all',
    filterDurum !== 'all',
    filterCari !== 'all',
    filterMinTutar !== '',
    filterMaxTutar !== ''
  ].filter(Boolean).length;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="faturalar-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accounting-faturalar">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Faturalar</h1>
            <p className="text-sm text-gray-500">Alınan ve kesilen faturaları görüntüleyin</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowEksikFaturaModal(true)}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
            data-testid="add-eksik-fatura-btn"
          >
            <ArrowDownCircle className="h-4 w-4" />
            Eksik Fatura Ekle
          </Button>
          <Button
            variant="outline"
            onClick={() => exportToExcel(activeTab === 'alinan' ? alinanFaturalar : kesilenFaturalar, activeTab)}
            className="flex items-center gap-2"
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Excel İndir
          </Button>
        </div>
      </div>

      {/* Summary Cards - En Üstte */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-500 rounded-lg">
                <ArrowDownCircle className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-emerald-600 font-medium">Alınan Faturalar</p>
                <p className="text-2xl font-bold text-emerald-900">{alinanFaturalar.length} adet</p>
                <p className="text-sm text-gray-600 mt-1">
                  Toplam: <span className="font-semibold text-emerald-700">{formatCurrency(alinanToplam, 'TL')}</span>
                  {eksikAlinanToplam > 0 && (
                    <span className="ml-2 text-red-600">
                      | Eksik: <span className="font-semibold">{formatCurrency(eksikAlinanToplam, 'TL')}</span>
                    </span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-500 rounded-lg">
                <ArrowUpCircle className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-orange-600 font-medium">Kesilen Faturalar</p>
                <p className="text-2xl font-bold text-orange-900">{kesilenFaturalar.length} adet</p>
                <p className="text-sm text-gray-600 mt-1">
                  Toplam: <span className="font-semibold text-orange-700">{formatCurrency(kesilenToplam, 'TL')}</span>
                  {eksikKesilenToplam > 0 && (
                    <span className="ml-2 text-red-600">
                      | Eksik: <span className="font-semibold">{formatCurrency(eksikKesilenToplam, 'TL')}</span>
                    </span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500 rounded-lg">
                <Receipt className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-blue-600 font-medium">Alınacak Faturalar</p>
                <p className="text-2xl font-bold text-blue-900">{alinacakFaturalar.length} adet</p>
                <p className="text-sm text-gray-600 mt-1">
                  Toplam: <span className="font-semibold text-blue-700">{formatCurrency(alinacakToplam, 'TL')}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-500 rounded-lg">
                <Receipt className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-amber-600 font-medium">Kesilecek Faturalar</p>
                <p className="text-2xl font-bold text-amber-900">{kesilecekFaturalar.length} adet</p>
                <p className="text-sm text-gray-600 mt-1">
                  Toplam: <span className="font-semibold text-amber-700">{formatCurrency(kesilecekToplam, 'TL')}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-500 rounded-lg">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-indigo-600 font-medium">Bu Ay KDV Durumu</p>
                <p className={`text-xl font-bold ${buAyKdvDurumu.netKDV > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {buAyKdvDurumu.netKDV > 0 ? 'AÇIK (ÖDENECEK)' : 'ARTI (DEVREDEN)'}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Fark: <span className={`font-semibold ${buAyKdvDurumu.netKDV > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{formatCurrency(Math.abs(buAyKdvDurumu.netKDV), 'TL')}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Üst satır - Arama ve temel filtreler */}
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Fatura no, cari hesap veya açıklama ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="fatura-search"
                  />
                </div>
              </div>
              <Select value={filterMonth} onValueChange={setFilterMonth}>
                <SelectTrigger className="w-[180px]" data-testid="fatura-month-filter">
                  <Calendar className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Ay Seç" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Aylar</SelectItem>
                  {getMonths().map(month => {
                    const [year, m] = month.split('-');
                    const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
                    return (
                      <SelectItem key={month} value={month}>
                        {monthNames[parseInt(m) - 1]} {year}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Select value={filterDurum} onValueChange={setFilterDurum}>
                <SelectTrigger className="w-[180px]" data-testid="fatura-durum-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Durum Seç" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Durumlar</SelectItem>
                  {getDurumlar().map(durum => (
                    <SelectItem key={durum} value={durum}>
                      {getDurumLabel(durum)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Alt satır - Cari hesap ve tutar filtreleri */}
            <div className="flex flex-wrap gap-4 items-end">
              <Select value={filterCari} onValueChange={setFilterCari}>
                <SelectTrigger className="w-[220px]" data-testid="fatura-cari-filter">
                  <Building2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Cari Hesap Seç" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Cari Hesaplar</SelectItem>
                  {getUniqueCariHesaplar().map(cari => (
                    <SelectItem key={cari} value={cari}>
                      {cari}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex items-center gap-2">
                <div className="w-[140px]">
                  <Input
                    type="number"
                    placeholder="Min Tutar"
                    value={filterMinTutar}
                    onChange={(e) => setFilterMinTutar(e.target.value)}
                    className="text-sm"
                    data-testid="filter-min-tutar"
                  />
                </div>
                <span className="text-gray-400">-</span>
                <div className="w-[140px]">
                  <Input
                    type="number"
                    placeholder="Max Tutar"
                    value={filterMaxTutar}
                    onChange={(e) => setFilterMaxTutar(e.target.value)}
                    className="text-sm"
                    data-testid="filter-max-tutar"
                  />
                </div>
              </div>

              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-gray-500 hover:text-gray-700"
                  data-testid="clear-filters-btn"
                >
                  <X className="h-4 w-4 mr-1" />
                  Filtreleri Temizle ({activeFilterCount})
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-5 mb-4">
          <TabsTrigger 
            value="alinan" 
            className="flex items-center gap-2"
            data-testid="tab-alinan-faturalar"
          >
            <ArrowDownCircle className="h-4 w-4" />
            Alınan Faturalar
            <Badge variant="secondary" className="ml-2">{alinanFaturalar.length}</Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="kesilen" 
            className="flex items-center gap-2"
            data-testid="tab-kesilen-faturalar"
          >
            <ArrowUpCircle className="h-4 w-4" />
            Kesilen Faturalar
            <Badge variant="secondary" className="ml-2">{kesilenFaturalar.length}</Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="alinacak" 
            className="flex items-center gap-2"
            data-testid="tab-alinacak-faturalar"
          >
            <Receipt className="h-4 w-4" />
            Alınacak Faturalar
            <Badge variant="secondary" className="ml-2">{alinacakFaturalar.length}</Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="kesilecek" 
            className="flex items-center gap-2"
            data-testid="tab-kesilecek-faturalar"
          >
            <Receipt className="h-4 w-4" />
            Kesilecek Faturalar
            <Badge variant="secondary" className="ml-2">{kesilecekFaturalar.length}</Badge>
          </TabsTrigger>
          <TabsTrigger 
            value="kdv_odemeleri" 
            className="flex items-center gap-2"
            data-testid="tab-kdv-odemeleri"
          >
            <FileText className="h-4 w-4" />
            KDV Ödemeleri
            <Badge variant="secondary" className="ml-2">{kdvOdemeleri.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Alınan Faturalar Tab */}
        <TabsContent value="alinan">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <ArrowDownCircle className="h-5 w-5 text-emerald-600" />
                  Alınan Faturalar
                </CardTitle>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                    Toplam: {formatCurrency(alinanToplam, 'TL')}
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportToExcel(alinanFaturalar, 'alinan')}
                    className="flex items-center gap-1"
                    data-testid="export-alinan-excel"
                  >
                    <Download className="h-4 w-4" />
                    Excel
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {alinanFaturalar.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Alınan fatura bulunamadı</p>
                  <p className="text-sm mt-1">Çıkışlarda fatura bilgisi eklendiğinde burada görünecektir</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-24">Takip No</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50 select-none"
                          onClick={() => handleSort('tarih')}
                        >
                          <div className="flex items-center">
                            Tarih
                            <SortIcon columnKey="tarih" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50 select-none"
                          onClick={() => handleSort('fatura_no')}
                        >
                          <div className="flex items-center">
                            Fatura No
                            <SortIcon columnKey="fatura_no" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50 select-none"
                          onClick={() => handleSort('cari')}
                        >
                          <div className="flex items-center">
                            Kimden
                            <SortIcon columnKey="cari" />
                          </div>
                        </TableHead>
                        <TableHead>Açıklama</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50 select-none"
                          onClick={() => handleSort('durum')}
                        >
                          <div className="flex items-center">
                            Durum
                            <SortIcon columnKey="durum" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer hover:bg-gray-50 select-none"
                          onClick={() => handleSort('tutar')}
                        >
                          <div className="flex items-center justify-end">
                            Anlaşılan Fatura Miktarı
                            <SortIcon columnKey="tutar" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer hover:bg-gray-50 select-none"
                          onClick={() => handleSort('fatura_tutari')}
                        >
                          <div className="flex items-center justify-end">
                            Fatura Tutarı
                            <SortIcon columnKey="fatura_tutari" />
                          </div>
                        </TableHead>
                        <TableHead className="text-right">KDV Tutarı</TableHead>
                        <TableHead className="text-right">Eksik Fatura</TableHead>
                        <TableHead>Belge</TableHead>
                        <TableHead className="text-center">Detay</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {alinanFaturalar.map((fatura) => {
                        const faturaTutariKdvDahil = parseFloat(fatura.fatura_miktar) || 0;
                        const kdvTutari = parseFloat(fatura.fatura_kdv) || parseFloat(fatura.kdv_tutari) || 0;
                        // Takipten anlaşılan fatura miktarını al (sadece takipte fatura_miktari varsa)
                        const takip = fatura.borc_takip_id ? borcTakipler.find(t => t.id === fatura.borc_takip_id) : null;
                        
                        // Anlaşılan fatura miktarı - önce işlemden, yoksa takipten
                        const anlasilanFaturaMiktari = fatura.anlaşılan_fatura_miktari 
                          ? parseFloat(fatura.anlaşılan_fatura_miktari) 
                          : (takip && takip.fatura_miktari ? parseFloat(takip.fatura_miktari) : null);
                        
                        // Eksik = Anlaşılan - Toplam alınan fatura (kdv dahil tutar üzerinden)
                        const toplamAlinanFatura = parseFloat(fatura.alinan_fatura_toplam) || faturaTutariKdvDahil;
                        const eksikFatura = anlasilanFaturaMiktari !== null && toplamAlinanFatura > 0 ? anlasilanFaturaMiktari - toplamAlinanFatura : null;
                        
                        // Seri no - takip varsa takip seri no, yoksa işlem seri no
                        const seriNo = takip?.seri_no || fatura.islem_no || fatura.seri_no || fatura.fatura_no;
                        
                        // Fatura tarihi (individual fatura için fatura_tarih kullan)
                        const faturaTarihi = fatura.fatura_tarih || fatura.fatura_tarihi || fatura.tarih;
                        
                        return (
                        <TableRow key={fatura.row_id || fatura.id} data-testid={`alinan-fatura-${fatura.row_id || fatura.id}`}>
                          <TableCell>
                            <div className="flex flex-col">
                              {seriNo ? (
                                <span className={`font-mono text-sm font-medium ${takip ? 'text-blue-600' : 'text-orange-600'}`}>{seriNo}</span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                              {fatura.is_individual_fatura && fatura.fatura_index && (
                                <>
                                  <span className="text-xs text-purple-600">Fatura #{fatura.fatura_index}</span>
                                  {fatura.fatura_index > 1 && (
                                    <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-1 rounded mt-0.5">Devam Faturası</span>
                                  )}
                                </>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {(() => {
                              const dt = formatDateTime(faturaTarihi, fatura.fatura_ekleme_tarihi || fatura.created_at);
                              return (
                                <div>
                                  <div>{dt.date}</div>
                                  {dt.time && <div className="text-xs text-gray-500">{dt.time}</div>}
                                </div>
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-sm">
                              {fatura.fatura_no || fatura.islem_no || '-'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-gray-400" />
                              {fatura.cari_hesap_adi || fatura.kime || '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            {(fatura.fatura_aciklama || fatura.aciklama) ? (
                              <button
                                onClick={() => {
                                  setSelectedAciklama(fatura.fatura_aciklama || fatura.aciklama);
                                  setShowAciklamaModal(true);
                                }}
                                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 cursor-pointer"
                                title="Açıklamayı görüntüle"
                              >
                                <FileText className="h-4 w-4" />
                                <span className="text-xs">Açıklama</span>
                              </button>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {getFaturaDurumuBadge(fatura.fatura_durumu)}
                          </TableCell>
                          <TableCell className="text-right font-medium text-gray-700">
                            {anlasilanFaturaMiktari !== null ? formatCurrency(anlasilanFaturaMiktari, fatura.para_birimi || 'TL') : '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium text-emerald-600">
                            {faturaTutariKdvDahil > 0 ? formatCurrency(faturaTutariKdvDahil, fatura.para_birimi || 'TL') : '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium text-purple-600">
                            {kdvTutari > 0 ? formatCurrency(kdvTutari, fatura.para_birimi || 'TL') : '-'}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${eksikFatura !== null && eksikFatura > 0 ? 'text-red-600' : eksikFatura !== null && eksikFatura < 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                            {eksikFatura !== null && eksikFatura !== 0 ? formatCurrency(Math.abs(eksikFatura), fatura.para_birimi || 'TL') : '-'}
                            {eksikFatura !== null && eksikFatura > 0 && <span className="text-xs ml-1">(eksik)</span>}
                            {eksikFatura !== null && eksikFatura < 0 && <span className="text-xs ml-1">(fazla)</span>}
                            {eksikFatura === 0 && anlasilanFaturaMiktari !== null && <span className="text-green-600">✓</span>}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              // Individual fatura için sadece kendi belgesini göster
                              if (fatura.is_individual_fatura && fatura.fatura_belge) {
                                return (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-blue-600 hover:text-blue-800"
                                    onClick={() => {
                                      const url = fatura.fatura_belge.url || fatura.fatura_belge;
                                      // Handle both relative and absolute URLs
                                      const fullUrl = url.startsWith('http') || url.startsWith('data:') 
                                        ? url 
                                        : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                                      window.open(fullUrl, '_blank');
                                    }}
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    1 belge
                                  </Button>
                                );
                              }
                              const belgeler = getAllBelgeler(fatura);
                              if (belgeler.length > 0) {
                                return (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => openBelgePreview(fatura, `${fatura.seri_no || fatura.islem_no || 'Fatura'} - Belgeler`)}
                                    className="text-blue-600 hover:text-blue-800"
                                    data-testid={`view-belgeler-${fatura.id}`}
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    {belgeler.length} belge
                                  </Button>
                                );
                              }
                              return <span className="text-gray-400 text-sm">-</span>;
                            })()}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDetailModal(fatura, 'alinan')}
                              className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                              data-testid={`view-detail-alinan-${fatura.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Kesilen Faturalar Tab */}
        <TabsContent value="kesilen">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <ArrowUpCircle className="h-5 w-5 text-orange-600" />
                  Kesilen Faturalar
                </CardTitle>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-orange-600 border-orange-600">
                    Toplam: {formatCurrency(kesilenToplam, 'TL')}
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => exportToExcel(kesilenFaturalar, 'kesilen')}
                    className="flex items-center gap-1"
                    data-testid="export-kesilen-excel"
                  >
                    <Download className="h-4 w-4" />
                    Excel
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {kesilenFaturalar.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Kesilen fatura bulunamadı</p>
                  <p className="text-sm mt-1">Girişlerde fatura bilgisi eklendiğinde burada görünecektir</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-24">Takip No</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50 select-none"
                          onClick={() => handleSort('tarih')}
                        >
                          <div className="flex items-center">
                            Tarih
                            <SortIcon columnKey="tarih" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50 select-none"
                          onClick={() => handleSort('fatura_no')}
                        >
                          <div className="flex items-center">
                            Fatura No
                            <SortIcon columnKey="fatura_no" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50 select-none"
                          onClick={() => handleSort('cari')}
                        >
                          <div className="flex items-center">
                            Kime
                            <SortIcon columnKey="cari" />
                          </div>
                        </TableHead>
                        <TableHead>Açıklama</TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-50 select-none"
                          onClick={() => handleSort('durum')}
                        >
                          <div className="flex items-center">
                            Durum
                            <SortIcon columnKey="durum" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer hover:bg-gray-50 select-none"
                          onClick={() => handleSort('tutar')}
                        >
                          <div className="flex items-center justify-end">
                            Anlaşılan Fatura Miktarı
                            <SortIcon columnKey="tutar" />
                          </div>
                        </TableHead>
                        <TableHead 
                          className="text-right cursor-pointer hover:bg-gray-50 select-none"
                          onClick={() => handleSort('fatura_tutari')}
                        >
                          <div className="flex items-center justify-end">
                            Fatura Tutarı
                            <SortIcon columnKey="fatura_tutari" />
                          </div>
                        </TableHead>
                        <TableHead className="text-right">KDV Tutarı</TableHead>
                        <TableHead className="text-right">Eksik Fatura</TableHead>
                        <TableHead>Belge</TableHead>
                        <TableHead className="text-center">Detay</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        // Aynı takip no'ya göre grupla ve kümülatif hesaplama için hazırla
                        const takipCumulatives = {};
                        
                        // Önce tüm faturaları ekleme tarihine göre sırala (en eski ilk)
                        const sortedForCalc = [...kesilenFaturalar].sort((a, b) => {
                          const dateA = new Date(a.fatura_ekleme_tarihi || a.created_at || a.fatura_tarih || a.tarih || 0);
                          const dateB = new Date(b.fatura_ekleme_tarihi || b.created_at || b.fatura_tarih || b.tarih || 0);
                          return dateA - dateB;
                        });
                        
                        // Her fatura için kümülatif toplamı hesapla
                        sortedForCalc.forEach((fatura) => {
                          const takipId = fatura.alacak_takip_id;
                          if (takipId) {
                            const faturaTutari = parseFloat(fatura.fatura_miktar) || 0;
                            if (!takipCumulatives[takipId]) {
                              takipCumulatives[takipId] = { total: 0, perFatura: {} };
                            }
                            // Bu faturadan ÖNCE kesilen toplam
                            takipCumulatives[takipId].perFatura[fatura.row_id || fatura.id] = takipCumulatives[takipId].total;
                            // Sonra bu faturayı ekle
                            takipCumulatives[takipId].total += faturaTutari;
                          }
                        });
                        
                        return kesilenFaturalar.map((fatura) => {
                          const faturaTutariKdvDahil = parseFloat(fatura.fatura_miktar) || 0;
                          const kdvTutari = parseFloat(fatura.fatura_kdv) || parseFloat(fatura.kdv_tutari) || 0;
                          // Takipten anlaşılan fatura miktarını al
                          const takip = fatura.alacak_takip_id ? alacakTakipler.find(t => t.id === fatura.alacak_takip_id) : null;
                          
                          // Anlaşılan fatura miktarı - önce işlemden, yoksa takipten
                          const anlasilanFaturaMiktari = fatura.anlaşılan_fatura_miktari 
                            ? parseFloat(fatura.anlaşılan_fatura_miktari) 
                            : (takip && takip.fatura_miktari ? parseFloat(takip.fatura_miktari) : null);
                          
                          // Bu fatura öncesi kesilen toplam
                          const previousTotal = fatura.alacak_takip_id && takipCumulatives[fatura.alacak_takip_id] 
                            ? takipCumulatives[fatura.alacak_takip_id].perFatura[fatura.row_id || fatura.id] || 0 
                            : 0;
                          
                          // Kalan miktar = Anlaşılan - Önceki faturalar toplamı
                          const kalanMiktar = anlasilanFaturaMiktari !== null ? anlasilanFaturaMiktari - previousTotal : null;
                          
                          // Eksik = Anlaşılan - Toplam kesilen (kdv dahil tutar üzerinden)
                          const toplamKesilenFatura = parseFloat(fatura.kesilen_fatura_toplam) || faturaTutariKdvDahil;
                          const eksikFatura = anlasilanFaturaMiktari !== null && toplamKesilenFatura > 0 ? anlasilanFaturaMiktari - toplamKesilenFatura : null;
                          
                          // Bu ilk fatura mı?
                          const isFirstForTakip = previousTotal === 0;
                          
                          // Seri no - takip varsa takip seri no, yoksa işlem seri no
                          const seriNo = takip?.seri_no || fatura.islem_no || fatura.seri_no || fatura.fatura_no;
                          
                          // Fatura tarihi (individual fatura için fatura_tarih kullan)
                          const faturaTarihi = fatura.fatura_tarih || fatura.fatura_tarihi || fatura.tarih;
                          
                          return (
                          <TableRow key={fatura.row_id || fatura.id} data-testid={`kesilen-fatura-${fatura.row_id || fatura.id}`}>
                            <TableCell>
                              <div className="flex flex-col">
                                {seriNo ? (
                                  <span className={`font-mono text-sm font-medium ${takip ? 'text-green-600' : 'text-blue-600'}`}>{seriNo}</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                                {fatura.is_individual_fatura && fatura.fatura_index && (
                                  <>
                                    <span className="text-xs text-purple-600">Fatura #{fatura.fatura_index}</span>
                                    {fatura.fatura_index > 1 && (
                                      <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-1 rounded mt-0.5">Devam Faturası</span>
                                    )}
                                  </>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {(() => {
                                const dt = formatDateTime(faturaTarihi, fatura.fatura_ekleme_tarihi || fatura.created_at);
                                return (
                                  <div>
                                    <div>{dt.date}</div>
                                    {dt.time && <div className="text-xs text-gray-500">{dt.time}</div>}
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <span className="font-mono text-sm">
                                {fatura.fatura_no || fatura.islem_no || '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-gray-400" />
                                {fatura.cari_hesap_adi || fatura.kimden || '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              {(fatura.fatura_aciklama || fatura.aciklama) ? (
                                <button
                                  onClick={() => {
                                    setSelectedAciklama(fatura.fatura_aciklama || fatura.aciklama);
                                    setShowAciklamaModal(true);
                                  }}
                                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 cursor-pointer"
                                  title="Açıklamayı görüntüle"
                                >
                                  <FileText className="h-4 w-4" />
                                  <span className="text-xs">Açıklama</span>
                                </button>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {getFaturaDurumuBadge(fatura.fatura_durumu)}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {anlasilanFaturaMiktari !== null ? (
                                <div>
                                  {isFirstForTakip ? (
                                    // İlk işlem: Orijinal anlaşılan miktarı göster
                                    <div className="text-gray-700">{formatCurrency(anlasilanFaturaMiktari, fatura.para_birimi || 'TL')}</div>
                                  ) : (
                                    // Sonraki işlemler: Kalan miktarı ana değer olarak göster
                                    <>
                                      <div className="text-gray-700">{formatCurrency(kalanMiktar, fatura.para_birimi || 'TL')}</div>
                                      <div className="text-xs text-gray-400 font-normal">
                                        (Anlaşılan: {formatCurrency(anlasilanFaturaMiktari, fatura.para_birimi || 'TL')})
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-right font-medium text-orange-600">
                              {faturaTutariKdvDahil > 0 ? formatCurrency(faturaTutariKdvDahil, fatura.para_birimi || 'TL') : '-'}
                            </TableCell>
                            <TableCell className="text-right font-medium text-purple-600">
                              {kdvTutari > 0 ? formatCurrency(kdvTutari, fatura.para_birimi || 'TL') : '-'}
                            </TableCell>
                            <TableCell className={`text-right font-medium ${eksikFatura !== null && eksikFatura > 0 ? 'text-red-600' : eksikFatura !== null && eksikFatura < 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                              {eksikFatura !== null && eksikFatura !== 0 ? formatCurrency(Math.abs(eksikFatura), fatura.para_birimi || 'TL') : '-'}
                              {eksikFatura !== null && eksikFatura > 0 && <span className="text-xs ml-1">(eksik)</span>}
                              {eksikFatura !== null && eksikFatura < 0 && <span className="text-xs ml-1">(fazla)</span>}
                              {eksikFatura === 0 && anlasilanFaturaMiktari !== null && <span className="text-green-600">✓</span>}
                            </TableCell>
                          <TableCell>
                            {(() => {
                              // Individual fatura için sadece kendi belgesini göster
                              if (fatura.is_individual_fatura && fatura.fatura_belge) {
                                return (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-blue-600 hover:text-blue-800"
                                    onClick={() => {
                                      const url = fatura.fatura_belge.url || fatura.fatura_belge;
                                      // Handle both relative and absolute URLs
                                      const fullUrl = url.startsWith('http') || url.startsWith('data:') 
                                        ? url 
                                        : `${process.env.REACT_APP_BACKEND_URL}${url}`;
                                      window.open(fullUrl, '_blank');
                                    }}
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    1 belge
                                  </Button>
                                );
                              }
                              const belgeler = getAllBelgeler(fatura);
                              if (belgeler.length > 0) {
                                return (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => openBelgePreview(fatura, `${fatura.seri_no || fatura.islem_no || 'Fatura'} - Belgeler`)}
                                    className="text-blue-600 hover:text-blue-800"
                                    data-testid={`view-belgeler-${fatura.row_id || fatura.id}`}
                                  >
                                    <FileText className="h-4 w-4 mr-1" />
                                    {belgeler.length} belge
                                  </Button>
                                );
                              }
                              return <span className="text-gray-400 text-sm">-</span>;
                            })()}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDetailModal(fatura, 'kesilen')}
                              className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                              data-testid={`view-detail-kesilen-${fatura.row_id || fatura.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                          </TableRow>
                        );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alınacak Faturalar Tab - Çıkışlardan alınması gereken faturalar */}
        <TabsContent value="alinacak">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-blue-600" />
                  Alınacak Faturalar
                </CardTitle>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-blue-600 border-blue-600">
                    Toplam: {formatCurrency(alinacakToplam, 'TL')}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {alinacakFaturalar.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Alınacak fatura bulunamadı</p>
                  <p className="text-sm mt-1">Çıkışlarda "Fatura Alınacak" durumu seçildiğinde burada görünecektir</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-blue-50">
                        <TableHead>Seri No</TableHead>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Kimden</TableHead>
                        <TableHead className="text-right">Tutar</TableHead>
                        <TableHead className="text-right">Alınacak Fatura</TableHead>
                        <TableHead>Açıklama</TableHead>
                        <TableHead>Şube</TableHead>
                        <TableHead className="text-center">Detay</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {alinacakFaturalar.map((fatura) => (
                        <TableRow key={fatura.id} data-testid={`alinacak-fatura-${fatura.id}`}>
                          <TableCell>
                            <span className="font-mono text-sm font-medium text-blue-600">
                              {fatura.seri_no || fatura.islem_no || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {fatura.tarih}
                            {fatura.saat && (
                              <span className="text-xs text-gray-500 ml-1">{fatura.saat}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{fatura.cari_hesap_adi || '-'}</span>
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(fatura.miktar, fatura.para_birimi || 'TL')}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="font-semibold text-blue-600">
                              {formatCurrency(fatura.anlaşılan_fatura_miktari || fatura.miktar, 'TL')}
                            </span>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate" title={fatura.aciklama}>
                            {fatura.aciklama || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {fatura.fortis_sube || fatura.sube || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDetailModal(fatura, 'alinacak')}
                              className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                              data-testid={`view-detail-alinacak-${fatura.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Kesilecek Faturalar Tab - Girişlerden kesilmesi gereken faturalar */}
        <TabsContent value="kesilecek">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-amber-600" />
                  Kesilecek Faturalar
                </CardTitle>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-amber-600 border-amber-600">
                    Toplam: {formatCurrency(kesilecekToplam, 'TL')}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {kesilecekFaturalar.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Receipt className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Kesilecek fatura bulunamadı</p>
                  <p className="text-sm mt-1">Girişlerde "Fatura Kesilecek" durumu seçildiğinde burada görünecektir</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-amber-50">
                        <TableHead>Seri No</TableHead>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Kime</TableHead>
                        <TableHead className="text-right">Tutar</TableHead>
                        <TableHead className="text-right">Kesilecek Fatura</TableHead>
                        <TableHead>Açıklama</TableHead>
                        <TableHead>Şube</TableHead>
                        <TableHead className="text-center">Detay</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {kesilecekFaturalar.map((fatura) => (
                        <TableRow key={fatura.id} data-testid={`kesilecek-fatura-${fatura.id}`}>
                          <TableCell>
                            <span className="font-mono text-sm font-medium text-amber-600">
                              {fatura.seri_no || fatura.islem_no || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {fatura.tarih}
                            {fatura.saat && (
                              <span className="text-xs text-gray-500 ml-1">{fatura.saat}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <span className="font-medium">{fatura.cari_hesap_adi || '-'}</span>
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(fatura.miktar, fatura.para_birimi || 'TL')}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="font-semibold text-amber-600">
                              {formatCurrency(fatura.anlaşılan_fatura_miktari || fatura.miktar, 'TL')}
                            </span>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate" title={fatura.aciklama}>
                            {fatura.aciklama || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {fatura.fortis_sube || fatura.sube || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDetailModal(fatura, 'kesilecek')}
                              className="text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                              data-testid={`view-detail-kesilecek-${fatura.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* KDV Ödemeleri Tab */}
        <TabsContent value="kdv_odemeleri">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-indigo-600" />
                  KDV Ödemeleri
                </CardTitle>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-indigo-600 border-indigo-600">
                    Toplam {kdvOdemeleri.length} İşlem
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {kdvOdemeleri.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>KDV Ödemesi etiketli işlem bulunamadı</p>
                  <p className="text-sm mt-1">İşlemlere 'KDV Ödemesi' etiketi eklediğinizde burada görünecektir</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Saat</TableHead>
                        <TableHead>İşlem Tipi</TableHead>
                        <TableHead>Miktar</TableHead>
                        <TableHead>Belge</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {kdvOdemeleri.map((islem) => (
                        <TableRow key={islem.id} data-testid={`kdv-odeme-${islem.id}`}>
                          <TableCell>
                            {formatDate(islem.tarih || islem.created_at)}
                          </TableCell>
                          <TableCell>
                            {islem.saat || formatDateTime(islem.created_at || new Date(), islem.created_at).time || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge className={islem.islem_tipi === 'Giriş (Tahsilat)' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}>
                              {islem.islem_tipi}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium text-gray-900">
                            {formatCurrency(islem.miktar, islem.para_birimi || 'TL')}
                          </TableCell>
                          <TableCell>
                            {islem.belgeler && islem.belgeler.length > 0 ? (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-blue-600 hover:text-blue-800"
                                onClick={() => openBelgePreview(islem, `Belgeler`)}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                {islem.belgeler.length} Belge
                              </Button>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Belge Preview Modal */}
      <Dialog open={showBelgeModal} onOpenChange={setShowBelgeModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {previewTitle}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {previewBelgeler.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Belge bulunamadı</p>
            ) : (
              <div className="grid gap-4">
                {previewBelgeler.map((belge, index) => {
                  // URL kontrolü - string olduğundan emin ol
                  const rawUrl = belge.url;
                  const belgeUrl = (typeof rawUrl === 'string') ? rawUrl : '';
                  const belgeName = belge.name || `Belge ${index + 1}`;
                  
                  // Belge tipini URL'den veya dosya adından çıkar
                  const isBase64 = belgeUrl && belgeUrl.startsWith('data:');
                  const getFileType = () => {
                    if (isBase64 && belgeUrl) {
                      const mimeMatch = belgeUrl.match(/^data:([^;]+);/);
                      return mimeMatch ? mimeMatch[1] : '';
                    }
                    const ext = belgeName.toLowerCase().split('.').pop() || '';
                    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
                    if (ext === 'pdf') return 'pdf';
                    return 'other';
                  };
                  
                  const mimeType = isBase64 && belgeUrl ? (belgeUrl.match(/^data:([^;]+);/)?.[1] || '') : '';
                  const isImage = (mimeType && mimeType.startsWith('image/')) || getFileType() === 'image';
                  const isPdf = mimeType === 'application/pdf' || getFileType() === 'pdf';
                  
                  // İndirme fonksiyonu
                  const handleDownload = () => {
                    if (!belgeUrl) {
                      console.warn('Belge URL bulunamadı');
                      return;
                    }
                    if (isBase64) {
                      // Base64'ü blob'a çevir ve indir
                      try {
                        const byteCharacters = atob(belgeUrl.split(',')[1]);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                          byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: mimeType || 'application/octet-stream' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = belgeName;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      } catch (e) {
                        console.error('İndirme hatası:', e);
                        window.open(belgeUrl, '_blank');
                      }
                    } else {
                      // Normal URL için yeni sekmede aç
                      window.open(belgeUrl, '_blank');
                    }
                  };
                  
                  return (
                    <div key={index} className="border rounded-lg p-4 hover:bg-gray-50" data-testid={`belge-item-${index}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-blue-600" />
                          <div>
                            <p className="font-medium">{belgeName}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Badge variant="outline" className="text-xs">
                                {belge.type}
                              </Badge>
                              {belge.tarih && (
                                <span>{formatDate(belge.tarih)}</span>
                              )}
                              {isImage && <Badge variant="secondary" className="text-xs">Resim</Badge>}
                              {isPdf && <Badge variant="secondary" className="text-xs">PDF</Badge>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDownload}
                            className="flex items-center gap-2"
                            data-testid={`download-belge-${index}`}
                          >
                            <Download className="h-4 w-4" />
                            İndir
                          </Button>
                          {!isBase64 && belgeUrl && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(belgeUrl, '_blank')}
                              className="flex items-center gap-2"
                              data-testid={`open-belge-${index}`}
                            >
                              <ExternalLink className="h-4 w-4" />
                              Aç
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Inline Önizleme */}
                      {isImage && belgeUrl && (
                        <div className="mt-3 border rounded-lg overflow-hidden bg-gray-100">
                          <img 
                            src={belgeUrl} 
                            alt={belgeName} 
                            className="max-w-full max-h-[400px] object-contain mx-auto"
                            data-testid={`preview-image-${index}`}
                          />
                        </div>
                      )}
                      
                      {isPdf && isBase64 && belgeUrl && (
                        <div className="mt-3 border rounded-lg overflow-hidden bg-gray-100">
                          <iframe
                            src={belgeUrl}
                            title={belgeName}
                            className="w-full h-[400px]"
                            data-testid={`preview-pdf-${index}`}
                          />
                        </div>
                      )}
                      
                      {isPdf && !isBase64 && belgeUrl && (
                        <div className="mt-3 border rounded-lg overflow-hidden bg-gray-100">
                          <iframe
                            src={belgeUrl}
                            title={belgeName}
                            className="w-full h-[400px]"
                            data-testid={`preview-pdf-${index}`}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Fatura Detay Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-indigo-600" />
              Fatura Detayları
              {selectedFatura && (
                <Badge variant="outline" className="ml-2">
                  {selectedFatura.seri_no || selectedFatura.islem_no || selectedFatura.fatura_no || '-'}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedFatura && (
            <div className="space-y-6">
              {/* Genel Bilgiler */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-700 border-b pb-2">Genel Bilgiler</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-gray-500">Tarih:</span>
                    <span className="font-medium">{formatDate(selectedFatura.fatura_tarihi || selectedFatura.tarih)}</span>
                    
                    <span className="text-gray-500">Fatura No:</span>
                    <span className="font-medium font-mono">{selectedFatura.fatura_no || selectedFatura.islem_no || '-'}</span>
                    
                    <span className="text-gray-500">Seri No:</span>
                    <span className="font-medium font-mono">{selectedFatura.seri_no || '-'}</span>
                    
                    <span className="text-gray-500">{selectedFatura.type === 'alinan' ? 'Kimden:' : 'Kime:'}</span>
                    <span className="font-medium">{selectedFatura.cari_hesap_adi || selectedFatura.kimden || selectedFatura.kime || '-'}</span>
                    
                    <span className="text-gray-500">Durum:</span>
                    <span>{getFaturaDurumuBadge(selectedFatura.fatura_durumu)}</span>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-700 border-b pb-2">Finansal Bilgiler</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {(() => {
                      const takip = selectedFatura.type === 'alinan' 
                        ? (selectedFatura.borc_takip_id ? borcTakipler.find(t => t.id === selectedFatura.borc_takip_id) : null)
                        : (selectedFatura.alacak_takip_id ? alacakTakipler.find(t => t.id === selectedFatura.alacak_takip_id) : null);
                      
                      // Önce işlemin kendi anlaşılan_fatura_miktari'sına bak, yoksa takipten al
                      const anlasilanFaturaMiktari = selectedFatura.anlaşılan_fatura_miktari 
                        ? parseFloat(selectedFatura.anlaşılan_fatura_miktari)
                        : (takip && takip.fatura_miktari ? parseFloat(takip.fatura_miktari) : null);
                      const anlasilanKaynagi = selectedFatura.anlaşılan_fatura_miktari ? 'işlem' : (takip && takip.fatura_miktari ? 'takip' : null);
                      
                      // Fatura listesini al
                      const faturaListesi = selectedFatura.type === 'kesilen' 
                        ? (selectedFatura.kesilen_faturalar || [])
                        : (selectedFatura.alinan_faturalar || []);
                      
                      // Önceki faturaların toplamı (son fatura hariç)
                      const oncekiFaturalarToplami = faturaListesi.length > 1 
                        ? faturaListesi.slice(0, -1).reduce((sum, f) => sum + (parseFloat(f.miktar) || 0), 0)
                        : 0;
                      
                      // Son (şimdiki) fatura bedeli
                      const simdikiFaturaBedeli = faturaListesi.length > 0 
                        ? parseFloat(faturaListesi[faturaListesi.length - 1].miktar) || 0
                        : parseFloat(selectedFatura.fatura_miktari) || 0;
                      
                      // Toplam kesilen/alınan fatura
                      const toplamFatura = parseFloat(selectedFatura.kesilen_fatura_toplam || selectedFatura.alinan_fatura_toplam) || 
                        faturaListesi.reduce((sum, f) => sum + (parseFloat(f.miktar) || 0), 0);
                      
                      // Kalan (Eksik) = Anlaşılan - Toplam
                      const eksikFatura = anlasilanFaturaMiktari !== null ? anlasilanFaturaMiktari - toplamFatura : null;
                      
                      return (
                        <>
                          <span className="text-gray-500">Anlaşılan Fatura Miktarı:</span>
                          <span className="font-medium text-purple-600">
                            {anlasilanFaturaMiktari !== null ? formatCurrency(anlasilanFaturaMiktari, selectedFatura.para_birimi || 'TL') : '-'}
                            {anlasilanKaynagi && <span className="text-xs text-purple-400 ml-1">({anlasilanKaynagi})</span>}
                          </span>
                          
                          {faturaListesi.length > 1 && (
                            <>
                              <span></span>
                              <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded">
                                📋 Devam Faturası ({faturaListesi.length}. fatura)
                              </span>
                              
                              <span className="text-gray-500">Önceki Fatura Toplamı:</span>
                              <span className="font-medium text-gray-600 text-right">
                                {formatCurrency(oncekiFaturalarToplami, selectedFatura.para_birimi || 'TL')}
                                <span className="text-xs text-gray-400 ml-1">({faturaListesi.length - 1} adet)</span>
                              </span>
                            </>
                          )}
                          
                          <span className="text-gray-500">
                            {faturaListesi.length > 1 ? 'Son Fatura Bedeli:' : 'Fatura Bedeli:'}
                          </span>
                          <span className="font-medium text-indigo-600 text-right">
                            {simdikiFaturaBedeli > 0 ? formatCurrency(simdikiFaturaBedeli, selectedFatura.para_birimi || 'TL') : '-'}
                          </span>
                          
                          <span className="text-gray-500 font-semibold">Toplam Kesilen:</span>
                          <span className="font-semibold text-indigo-700 text-right">
                            {formatCurrency(toplamFatura, selectedFatura.para_birimi || 'TL')}
                          </span>
                          
                          <span className="text-gray-500 font-semibold">Kalan (Eksik):</span>
                          <span className={`font-semibold text-right ${eksikFatura !== null && eksikFatura > 0 ? 'text-red-600' : eksikFatura !== null && eksikFatura < 0 ? 'text-blue-600' : 'text-green-600'}`}>
                            {eksikFatura !== null ? (
                              eksikFatura !== 0 ? (
                                <>
                                  {formatCurrency(Math.abs(eksikFatura), selectedFatura.para_birimi || 'TL')}
                                  <span className="text-xs ml-1">({eksikFatura > 0 ? 'eksik' : 'fazla'})</span>
                                </>
                              ) : <span className="text-green-600">✓ Tamamlandı</span>
                            ) : '-'}
                          </span>
                          
                          <span className="text-gray-500">Para Birimi:</span>
                          <span className="font-medium text-right">{selectedFatura.para_birimi || 'TL'}</span>
                          
                          <span className="text-gray-500">Hesap:</span>
                          <span className="font-medium">{selectedFatura.hesap_adi || '-'}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
              
              {/* Açıklama */}
              <div className="space-y-2">
                <h4 className="font-semibold text-gray-700 border-b pb-2">Açıklama</h4>
                <div className="bg-gray-50 p-4 rounded-lg min-h-[60px]">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedFatura.aciklama || 'Açıklama girilmemiş'}
                  </p>
                </div>
              </div>
              
              {/* Fatura Belgeleri */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-700 border-b pb-2 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Fatura Belgeleri
                </h4>
                {(() => {
                  const belgeler = getAllBelgeler(selectedFatura);
                  if (belgeler.length === 0) {
                    return (
                      <div className="text-center py-4 text-gray-400 bg-gray-50 rounded-lg">
                        <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Fatura belgesi bulunamadı</p>
                      </div>
                    );
                  }
                  return (
                    <div className="grid gap-2">
                      {belgeler.map((belge, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-blue-500" />
                            <div>
                              <p className="font-medium text-sm">{belge.name || `Belge ${index + 1}`}</p>
                              <p className="text-xs text-gray-500">{belge.type}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {belge.url && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const fullUrl = belge.url.startsWith('http') || belge.url.startsWith('data:')
                                      ? belge.url
                                      : `${process.env.REACT_APP_BACKEND_URL}${belge.url}`;
                                    window.open(fullUrl, '_blank');
                                  }}
                                  className="text-xs"
                                >
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                  Aç
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const fullUrl = belge.url.startsWith('http') || belge.url.startsWith('data:')
                                      ? belge.url
                                      : `${process.env.REACT_APP_BACKEND_URL}${belge.url}`;
                                    const a = document.createElement('a');
                                    a.href = fullUrl;
                                    a.download = belge.name || 'belge';
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                  }}
                                  className="text-xs"
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  İndir
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              
              {/* Takip Belgeleri */}
              {(selectedFatura.borc_takip_id || selectedFatura.alacak_takip_id || selectedFatura.takipteki_borc) && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-700 border-b pb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-orange-500" />
                    Takip Belgeleri
                    <Badge variant="secondary" className="text-xs">
                      {selectedFatura.type === 'alinan' ? 'Borç Takip' : 'Alacak Takip'}
                    </Badge>
                  </h4>
                  {loadingTakip ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                      <span className="ml-2 text-sm text-gray-500">Yükleniyor...</span>
                    </div>
                  ) : takipBelgeler.length === 0 ? (
                    <div className="text-center py-4 text-gray-400 bg-orange-50 rounded-lg">
                      <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Takip belgesi bulunamadı</p>
                    </div>
                  ) : (
                    <div className="grid gap-2">
                      {takipBelgeler.map((belge, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-orange-500" />
                            <div>
                              <p className="font-medium text-sm">{belge.name || belge.adi || `Takip Belgesi ${index + 1}`}</p>
                              <p className="text-xs text-gray-500">{belge.type}</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {(belge.url || belge.data) && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const rawUrl = belge.url || belge.data;
                                    const fullUrl = rawUrl.startsWith('http') || rawUrl.startsWith('data:')
                                      ? rawUrl
                                      : `${process.env.REACT_APP_BACKEND_URL}${rawUrl}`;
                                    window.open(fullUrl, '_blank');
                                  }}
                                  className="text-xs"
                                >
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                  Aç
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const rawUrl = belge.url || belge.data;
                                    const fullUrl = rawUrl.startsWith('http') || rawUrl.startsWith('data:')
                                      ? rawUrl
                                      : `${process.env.REACT_APP_BACKEND_URL}${rawUrl}`;
                                    const a = document.createElement('a');
                                    a.href = fullUrl;
                                    a.download = belge.name || belge.adi || 'belge';
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                  }}
                                  className="text-xs"
                                >
                                  <Download className="h-3 w-3 mr-1" />
                                  İndir
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Kesilen/Alınan Faturalar Listesi */}
              {(() => {
                const faturaListesi = selectedFatura.type === 'kesilen' 
                  ? (selectedFatura.kesilen_faturalar || [])
                  : (selectedFatura.alinan_faturalar || []);
                
                if (faturaListesi.length === 0) return null;
                
                return (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-700 border-b pb-2 flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-purple-500" />
                      {selectedFatura.type === 'kesilen' ? 'Kesilen Faturalar' : 'Alınan Faturalar'} Listesi
                      <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                        {faturaListesi.length} adet
                      </Badge>
                    </h4>
                    <div className="space-y-2">
                      {faturaListesi.map((fatura, index) => (
                        <div 
                          key={index} 
                          className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-100"
                          data-testid={`fatura-listesi-item-${index}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center w-8 h-8 bg-purple-200 rounded-full text-purple-700 font-semibold text-sm">
                              {index + 1}
                            </div>
                            <div className="space-y-1">
                              <div className="flex items-center gap-3">
                                <span className="font-medium text-purple-900">
                                  {formatCurrency(parseFloat(fatura.miktar || fatura.fatura_miktari || 0), selectedFatura.para_birimi || 'TL')}
                                </span>
                                {fatura.tarih && (
                                  <span className="text-sm text-gray-500">
                                    {formatDate(fatura.tarih)}
                                  </span>
                                )}
                              </div>
                              {fatura.aciklama && (
                                <p className="text-sm text-gray-600">{fatura.aciklama}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {fatura.belge && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const url = fatura.belge.startsWith('data:') ? fatura.belge : `${process.env.REACT_APP_BACKEND_URL}${fatura.belge}`;
                                  window.open(url, '_blank');
                                }}
                                className="text-xs text-purple-600 border-purple-300 hover:bg-purple-100"
                              >
                                <FileText className="h-3 w-3 mr-1" />
                                Belge
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      
                      {/* Toplam */}
                      <div className="flex items-center justify-between p-3 bg-purple-100 rounded-lg border border-purple-200 mt-2">
                        <span className="font-semibold text-purple-800">Toplam:</span>
                        <span className="font-bold text-purple-900 text-lg">
                          {formatCurrency(
                            faturaListesi.reduce((sum, f) => sum + (parseFloat(f.miktar || f.fatura_miktari || 0)), 0),
                            selectedFatura.para_birimi || 'TL'
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Eksik Fatura Ekleme Modal */}
      <Dialog open={showEksikFaturaModal} onOpenChange={setShowEksikFaturaModal}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowDownCircle className="h-5 w-5 text-emerald-600" />
              Eksik Fatura Tamamla
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden flex flex-col space-y-4">
            {/* Arama */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Takip No, Cari Hesap veya Seri No ile ara..."
                value={eksikFaturaSearchTerm}
                onChange={(e) => setEksikFaturaSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="eksik-fatura-search"
              />
            </div>
            
            {/* Tab seçimi */}
            <Tabs defaultValue="kesilen" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="kesilen" className="flex items-center gap-2">
                  <ArrowUpCircle className="h-4 w-4" />
                  Kesilen
                  <Badge variant="secondary">{eksikKesilenFaturalar.length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="alinan" className="flex items-center gap-2">
                  <ArrowDownCircle className="h-4 w-4" />
                  Alınan
                  <Badge variant="secondary">{eksikAlinanFaturalar.length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="alinacak" className="flex items-center gap-2">
                  <Receipt className="h-4 w-4" />
                  Alınacak
                  <Badge variant="secondary">{eksikAlinacakFaturalar.length}</Badge>
                </TabsTrigger>
                <TabsTrigger value="kesilecek" className="flex items-center gap-2">
                  <Receipt className="h-4 w-4" />
                  Kesilecek
                  <Badge variant="secondary">{eksikKesilecekFaturalar.length}</Badge>
                </TabsTrigger>
              </TabsList>
              
              {/* Kesilen Faturalar Listesi */}
              <TabsContent value="kesilen" className="flex-1 overflow-hidden">
                <ScrollArea className="h-[300px] border rounded-lg">
                  {eksikKesilenFaturalar.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 py-8">
                      <FileText className="h-12 w-12 mb-2 opacity-50" />
                      <p>Eksik kesilen fatura bulunamadı</p>
                    </div>
                  ) : (
                    <div className="p-2 space-y-2">
                      {eksikKesilenFaturalar.map((fatura) => (
                        <div
                          key={fatura.id}
                          onClick={() => {
                            setSelectedEksikFatura(fatura);
                            const miktar = fatura.eksikMiktar.toString();
                            setYeniFaturaMiktari(miktar);
                            setYeniFaturaKdvOrani('20');
                            const oran = 0.20;
                            const kdvDahilTutar = parseFloat(miktar);
                            const kdvTutari = kdvDahilTutar - (kdvDahilTutar / (1 + oran));
                            setYeniFaturaKdv(kdvTutari.toFixed(2));
                          }}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedEksikFatura?.id === fatura.id
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-gray-200 hover:border-emerald-300 hover:bg-gray-50'
                          }`}
                          data-testid={`eksik-fatura-item-${fatura.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex flex-col">
                                <span className="font-mono text-sm font-bold text-emerald-700">
                                  {fatura.takipSeriNo || fatura.seri_no}
                                </span>
                                <span className="text-xs text-gray-500">{fatura.displayName}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500">Anlaşılan: ₺{(parseFloat(fatura.anlasilanMiktar) || 0).toLocaleString('tr-TR')}</div>
                              <div className="text-xs text-emerald-600">Kesilen: ₺{(parseFloat(fatura.kesilenMiktar) || 0).toLocaleString('tr-TR')}</div>
                              <div className="text-sm font-bold text-red-600">Eksik: ₺{(parseFloat(fatura.eksikMiktar) || 0).toLocaleString('tr-TR')}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              {/* Alınan Faturalar Listesi */}
              <TabsContent value="alinan" className="flex-1 overflow-hidden">
                <ScrollArea className="h-[300px] border rounded-lg">
                  {eksikAlinanFaturalar.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 py-8">
                      <FileText className="h-12 w-12 mb-2 opacity-50" />
                      <p>Eksik alınan fatura bulunamadı</p>
                    </div>
                  ) : (
                    <div className="p-2 space-y-2">
                      {eksikAlinanFaturalar.map((fatura) => (
                        <div
                          key={fatura.id}
                          onClick={() => {
                            setSelectedEksikFatura(fatura);
                            const miktar = fatura.eksikMiktar.toString();
                            setYeniFaturaMiktari(miktar);
                            setYeniFaturaKdvOrani('20');
                            const oran = 0.20;
                            const kdvDahilTutar = parseFloat(miktar);
                            const kdvTutari = kdvDahilTutar - (kdvDahilTutar / (1 + oran));
                            setYeniFaturaKdv(kdvTutari.toFixed(2));
                          }}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedEksikFatura?.id === fatura.id
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-orange-300 hover:bg-gray-50'
                          }`}
                          data-testid={`eksik-fatura-item-${fatura.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex flex-col">
                                <span className="font-mono text-sm font-bold text-orange-700">
                                  {fatura.takipSeriNo || fatura.seri_no}
                                </span>
                                <span className="text-xs text-gray-500">{fatura.displayName}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500">Anlaşılan: ₺{(parseFloat(fatura.anlasilanMiktar) || 0).toLocaleString('tr-TR')}</div>
                              <div className="text-xs text-orange-600">Alınan: ₺{(parseFloat(fatura.alinanMiktar) || 0).toLocaleString('tr-TR')}</div>
                              <div className="text-sm font-bold text-red-600">Eksik: ₺{(parseFloat(fatura.eksikMiktar) || 0).toLocaleString('tr-TR')}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              {/* Alınacak Faturalar Listesi */}
              <TabsContent value="alinacak" className="flex-1 overflow-hidden">
                <ScrollArea className="h-[300px] border rounded-lg">
                  {eksikAlinacakFaturalar.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 py-8">
                      <Receipt className="h-12 w-12 mb-2 opacity-50" />
                      <p>Alınacak fatura bulunamadı</p>
                    </div>
                  ) : (
                    <div className="p-2 space-y-2">
                      {eksikAlinacakFaturalar.map((fatura) => (
                        <div
                          key={fatura.id}
                          onClick={() => {
                            setSelectedEksikFatura(fatura);
                            const miktar = (parseFloat(fatura.alinacakMiktar) || 0).toString();
                            setYeniFaturaMiktari(miktar);
                            setYeniFaturaKdvOrani('20');
                            const oran = 0.20;
                            const kdvDahilTutar = parseFloat(miktar);
                            const kdvTutari = kdvDahilTutar - (kdvDahilTutar / (1 + oran));
                            setYeniFaturaKdv(kdvTutari.toFixed(2));
                          }}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedEksikFatura?.id === fatura.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                          data-testid={`alinacak-fatura-item-${fatura.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex flex-col">
                                <span className="font-mono text-sm font-bold text-blue-700">
                                  {fatura.takipSeriNo || fatura.seri_no}
                                </span>
                                <span className="text-xs text-gray-500">{fatura.displayName}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500">Tutar: ₺{(parseFloat(fatura.miktar) || 0).toLocaleString('tr-TR')}</div>
                              <div className="text-sm font-bold text-blue-600">Alınacak: ₺{(parseFloat(fatura.alinacakMiktar) || 0).toLocaleString('tr-TR')}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
              
              {/* Kesilecek Faturalar Listesi */}
              <TabsContent value="kesilecek" className="flex-1 overflow-hidden">
                <ScrollArea className="h-[300px] border rounded-lg">
                  {eksikKesilecekFaturalar.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 py-8">
                      <Receipt className="h-12 w-12 mb-2 opacity-50" />
                      <p>Kesilecek fatura bulunamadı</p>
                    </div>
                  ) : (
                    <div className="p-2 space-y-2">
                      {eksikKesilecekFaturalar.map((fatura) => (
                        <div
                          key={fatura.id}
                          onClick={() => {
                            setSelectedEksikFatura(fatura);
                            const miktar = (parseFloat(fatura.kesilecekMiktar) || 0).toString();
                            setYeniFaturaMiktari(miktar);
                            setYeniFaturaKdvOrani('20');
                            const oran = 0.20;
                            const kdvDahilTutar = parseFloat(miktar);
                            const kdvTutari = kdvDahilTutar - (kdvDahilTutar / (1 + oran));
                            setYeniFaturaKdv(kdvTutari.toFixed(2));
                          }}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedEksikFatura?.id === fatura.id
                              ? 'border-amber-500 bg-amber-50'
                              : 'border-gray-200 hover:border-amber-300 hover:bg-gray-50'
                          }`}
                          data-testid={`kesilecek-fatura-item-${fatura.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="flex flex-col">
                                <span className="font-mono text-sm font-bold text-amber-700">
                                  {fatura.takipSeriNo || fatura.seri_no}
                                </span>
                                <span className="text-xs text-gray-500">{fatura.displayName}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500">Tutar: ₺{(parseFloat(fatura.miktar) || 0).toLocaleString('tr-TR')}</div>
                              <div className="text-sm font-bold text-amber-600">Kesilecek: ₺{(parseFloat(fatura.kesilecekMiktar) || 0).toLocaleString('tr-TR')}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
            
            {/* Seçili fatura bilgisi ve form */}
            {selectedEksikFatura && (
              <div className="border-t pt-4 space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">Seçili Kayıt:</span>
                    <Badge className={
                      selectedEksikFatura.type === 'kesilen' ? 'bg-emerald-500' : 
                      selectedEksikFatura.type === 'kesilecek' ? 'bg-amber-500' : 'bg-orange-500'
                    }>
                      {selectedEksikFatura.takipSeriNo || selectedEksikFatura.seri_no}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Cari:</span>
                      <span className="ml-1 font-medium">{selectedEksikFatura.displayName}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{selectedEksikFatura.type === 'kesilecek' ? 'Tutar:' : 'Anlaşılan:'}</span>
                      <span className="ml-1 font-medium">
                        ₺{(parseFloat(selectedEksikFatura.anlasilanMiktar) || parseFloat(selectedEksikFatura.miktar) || 0).toLocaleString('tr-TR')}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">{selectedEksikFatura.type === 'kesilecek' ? 'Kesilecek:' : selectedEksikFatura.type === 'alinacak' ? 'Alınacak:' : 'Eksik:'}</span>
                      <span className={`ml-1 font-bold ${selectedEksikFatura.type === 'kesilecek' ? 'text-amber-600' : selectedEksikFatura.type === 'alinacak' ? 'text-blue-600' : 'text-red-600'}`}>
                        ₺{(parseFloat(selectedEksikFatura.eksikMiktar) || parseFloat(selectedEksikFatura.kesilecekMiktar) || parseFloat(selectedEksikFatura.alinacakMiktar) || 0).toLocaleString('tr-TR')}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Fatura Miktarı (₺)</label>
                    <Input
                      type="number"
                      value={yeniFaturaMiktari}
                      onChange={(e) => {
                        const miktar = e.target.value;
                        setYeniFaturaMiktari(miktar);
                        if (miktar && yeniFaturaKdvOrani) {
                          const oran = parseFloat(yeniFaturaKdvOrani) / 100;
                          const kdvDahilTutar = parseFloat(miktar);
                          const kdvTutari = kdvDahilTutar - (kdvDahilTutar / (1 + oran));
                          setYeniFaturaKdv(kdvTutari.toFixed(2));
                        } else {
                          setYeniFaturaKdv('');
                        }
                      }}
                      placeholder="0.00"
                      data-testid="yeni-fatura-miktari"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">KDV Oranı (%)</label>
                    <Input
                      type="number"
                      value={yeniFaturaKdvOrani}
                      onChange={(e) => {
                        const oranVal = e.target.value;
                        setYeniFaturaKdvOrani(oranVal);
                        if (yeniFaturaMiktari && oranVal) {
                          const oran = parseFloat(oranVal) / 100;
                          const kdvDahilTutar = parseFloat(yeniFaturaMiktari);
                          const kdvTutari = kdvDahilTutar - (kdvDahilTutar / (1 + oran));
                          setYeniFaturaKdv(kdvTutari.toFixed(2));
                        } else {
                          setYeniFaturaKdv('');
                        }
                      }}
                      placeholder="20"
                      data-testid="yeni-fatura-kdv-orani"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">KDV Tutarı (₺)</label>
                    <Input
                      type="number"
                      value={yeniFaturaKdv}
                      onChange={(e) => setYeniFaturaKdv(e.target.value)}
                      placeholder="0.00"
                      data-testid="yeni-fatura-kdv"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Fatura Tarihi</label>
                    <Input
                      type="date"
                      value={yeniFaturaTarihi}
                      onChange={(e) => setYeniFaturaTarihi(e.target.value)}
                      data-testid="yeni-fatura-tarihi"
                    />
                  </div>
                </div>
                
                {/* Açıklama alanı */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Açıklama</label>
                  <Input
                    value={yeniFaturaAciklama}
                    onChange={(e) => setYeniFaturaAciklama(e.target.value)}
                    placeholder="Fatura açıklaması..."
                    data-testid="yeni-fatura-aciklama"
                  />
                </div>
                
                {/* Belge yükleme alanı */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Fatura Belgesi</label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => setYeniFaturaBelge(e.target.files[0])}
                      className="flex-1"
                      data-testid="yeni-fatura-belge"
                    />
                    {yeniFaturaBelge && (
                      <Badge variant="outline" className="text-emerald-600">
                        {yeniFaturaBelge.name}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">PDF, JPG veya PNG formatında yükleyebilirsiniz</p>
                </div>
                
                <div className="flex justify-end gap-2">
                    <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedEksikFatura(null);
                      setYeniFaturaMiktari('');
                      setYeniFaturaKdvOrani('20');
                      setYeniFaturaKdv('');
                      setYeniFaturaAciklama('');
                      setYeniFaturaBelge(null);
                    }}
                  >
                    İptal
                  </Button>
                  <Button
                    onClick={handleAddEksikFatura}
                    disabled={savingFatura || !yeniFaturaMiktari}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="save-eksik-fatura-btn"
                  >
                    {savingFatura ? 'Kaydediliyor...' : 'Fatura Ekle'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Açıklama Modal */}
      <Dialog open={showAciklamaModal} onOpenChange={setShowAciklamaModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Açıklama
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-gray-50 p-4 rounded-lg border">
              <p className="text-gray-700 whitespace-pre-wrap">{selectedAciklama || 'Açıklama bulunamadı'}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountingFaturalar;
